<script setup lang="ts" generic="T">
import type {
  ISelectSectionSimpleTreeExpose,
  ISelectSectionSimpleTreeNodeTag,
} from "#/ui-kit/components/sections/select-section-simple-tree.types";
import type {
  IVirtualizedTreeNode,
  IVirtualizedTreeNodeModel,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.types";

import SelectSectionSimpleTree from "#/ui-kit/components/sections/select-section-simple-tree.vue";
import { useCommonFrontendIntl } from "#/ui-kit/locales/typed-intl";
import { DATA_TEST_IDS, isDefined } from "@voltalis/frontend-utils";
import { computed, ref, useTemplateRef, watch } from "vue";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Skeleton from "primevue/skeleton";

export interface ISearchableSelectSectionSimpleTreeProps<T> {
  siteSectionsTree?: IVirtualizedTreeNode<T | null>;
  initialSelectedSection: T | null;
  showRadioButtonCb?: (node: IVirtualizedTreeNode<T | null>) => boolean;
  isNodeDisabled?: (node: IVirtualizedTreeNode<T | null>) => boolean;
  getNodeTagsCb?: (node: IVirtualizedTreeNode<T | null>) => ISelectSectionSimpleTreeNodeTag[];
  emitInitialSelection?: boolean;
}

export interface ISearchableSelectSectionSimpleTreeEmits<T> {
  (e: "selection", data: T | null): void;
}

const props = defineProps<ISearchableSelectSectionSimpleTreeProps<T>>();
const emits = defineEmits<ISearchableSelectSectionSimpleTreeEmits<T>>();

const { t, k } = useCommonFrontendIntl();

const searchQuery = ref<string>("");
const selectSectionSimpleTree = useTemplateRef<ISelectSectionSimpleTreeExpose<T>>("selectSectionSimpleTree");

async function forceUpdate(): Promise<void> {
  await selectSectionSimpleTree.value?.forceUpdate();
}

function scrollToNode(findNodeFn: (node: IVirtualizedTreeNodeModel<T | null>) => boolean): void {
  selectSectionSimpleTree.value?.scrollToNode(findNodeFn);
}

function filterSectionByName(query: string, node: IVirtualizedTreeNode<T | null>): boolean {
  const nodeName = `${node.name}`;
  return nodeName.toLowerCase().includes(query.toLowerCase());
}

const filteredNodes = computed<Array<IVirtualizedTreeNode<T | null>>>(() => {
  if (!props.siteSectionsTree) {
    return [];
  }

  if (searchQuery.value.length === 0) {
    return props.siteSectionsTree.children ?? [];
  }

  function buildNode(node: IVirtualizedTreeNode<T | null>): IVirtualizedTreeNode<T | null> | null {
    const isValid = filterSectionByName(searchQuery.value, node);

    if (isValid) {
      return node;
    }

    if (!node.children) {
      return null;
    }

    const filteredChildren = node.children.map((n) => buildNode(n)).filter((node) => isDefined(node));
    if (filteredChildren.length === 0) {
      return null;
    }

    return {
      ...node,
      children: filteredChildren,
    };
  }

  return props.siteSectionsTree.children?.map((n) => buildNode(n)).filter((node) => isDefined(node)) ?? [];
});

const siteStructure = computed<IVirtualizedTreeNode<T | null> | undefined>(() => {
  if (!props.siteSectionsTree) return undefined;

  return {
    ...props.siteSectionsTree,
    children: filteredNodes.value,
  };
});

watch(
  filteredNodes,
  () => {
    void forceUpdate();
  },
  {
    flush: "post",
  },
);

defineExpose<ISelectSectionSimpleTreeExpose<T>>({
  forceUpdate,
  scrollToNode,
});
</script>

<template>
  <IconField>
    <InputIcon class="pi pi-search" />
    <InputText
      :model-value="searchQuery"
      class="w-full"
      :placeholder="t(k.commonFrontend['site-tree-structure'].searchSection)"
      :pt="{
        root: {
          'data-test-id': DATA_TEST_IDS.components.searchableSelectSectionSimpleTree.searchTextInput,
        },
      }"
      @update:model-value="searchQuery = $event ?? ''"
    />
  </IconField>

  <Skeleton
    v-if="!siteStructure"
    class="!h-full w-full"
    :pt="{
      root: {
        'data-test-id': DATA_TEST_IDS.components.searchableSelectSectionSimpleTree.skeleton,
      },
    }"
  />
  <SelectSectionSimpleTree
    v-else
    ref="selectSectionSimpleTree"
    :siteSectionsTree="siteStructure"
    :initialSelectedSection="props.initialSelectedSection"
    :showRadioButtonCb="props.showRadioButtonCb"
    :isNodeDisabled="props.isNodeDisabled"
    :getNodeTagsCb="props.getNodeTagsCb"
    :emitInitialSelection="props.emitInitialSelection"
    @selection="emits('selection', $event)"
  />
</template>
